import { AttioDealResponse, AttioTeamMemberResponse, AttioWorkspaceMemberResponse } from '../attio.types';
import { BaseAttioService } from './BaseAttioService';

// Service-specific types
export interface AttioEngagementManagerData {
  name: string;
  imageSrc: string;
}

export interface AttioValuationEngagementManagerData {
  name: string;
  imageSrc: string;
}

export class AttioEngagementService extends BaseAttioService {
  constructor() {
    super('AttioEngagementService');
  }
  /**
   * Get engagement manager details directly by engagement manager record ID
   */
  async getEngagementManager(engagementManagerRecordId: string): Promise<AttioEngagementManagerData> {
    try {
      if (engagementManagerRecordId) {
        // Get engagement manager details directly
        const engagementManagerResponse = await this.get<AttioWorkspaceMemberResponse>(
          this.endpoints.retrieveWorkspaceMemberByRecordId(engagementManagerRecordId)
        );

        // Handle nested structure for workspace member data
        const engagementManagerData = engagementManagerResponse.data;

        return {
          name: `${engagementManagerData.first_name} ${engagementManagerData.last_name}`,
          imageSrc: engagementManagerData.avatar_url || '-',
        };
      }
      // Default values if no engagement manager ID provided
      return {
        name: '-',
        imageSrc: '-',
      };
    } catch (error) {
      console.warn('Error fetching engagement manager details:', error);
      return {
        name: '-',
        imageSrc: '-',
      };
    }
  }

  /**
   * Get valuation engagement manager details directly by engagement manager target record ID
   */
  async getValuationEngagementManager(engagementManagerRecordId: string): Promise<AttioValuationEngagementManagerData> {
    try {
      if (engagementManagerRecordId) {
        // Get engagement manager details directly
        const engagementManagerResponse = await this.get<AttioTeamMemberResponse>(
          this.endpoints.retrieveTeamByRecordId(engagementManagerRecordId)
        );
  
        // Handle nested structure for workspace member data
        const engagementManagerData = engagementManagerResponse.data.values;
  
        return {
          name: Array.isArray(engagementManagerData?.name) && engagementManagerData.name[0]?.value
            ? engagementManagerData.name[0].value
            : '-',
          imageSrc: Array.isArray(engagementManagerData?.profile_image_url) && engagementManagerData.profile_image_url[0]?.value
            ? engagementManagerData.profile_image_url[0].value
            : '-',
        };
      }
      // Default values if no engagement manager ID provided
      return {
        name: '-',
        imageSrc: '-',
      };
    } catch (error) {
      console.warn('Error fetching engagement manager details:', error);
      return {
        name: '-',
        imageSrc: '-',
      };
    }
  }

  /**
   * Get engagement manager details for a valuation record through deal ID
   */
  async getEngagementManagerByDealId(dealRecordId: string): Promise<AttioEngagementManagerData> {
    try {
      if (dealRecordId) {
        // Get deal details to find engagement manager
        try {
          const dealResponse = await this.get<AttioDealResponse>(this.endpoints.retrieveDealByRecordId(dealRecordId));

          const dealResponseData = dealResponse.data;
          const { values: dealData } = dealResponseData;

          const engagementManagerRecordId = dealData?.engagement_manager?.[0]?.referenced_actor_id;

          if (engagementManagerRecordId) {
            // Use the direct method to get engagement manager details
            return await this.getEngagementManager(engagementManagerRecordId);
          }
        } catch (error) {
          console.warn('Error fetching engagement manager details:', error);
          return {
            name: '-',
            imageSrc: '-',
          };
        }
      }
      // Default values if any step fails
      return {
        name: '-',
        imageSrc: '-',
      };
    } catch (error) {
      console.warn('Error fetching engagement manager details:', error);
      return {
        name: '-',
        imageSrc: '-',
      };
    }
  }
}
